import { ContentLanguageCode, DEFAULT_LANGUAGE } from '@/shared/constants';
import { getMultilingualValue, getMultilingualValueForLang, multilingualRecordHasValue } from '@/shared/utils/languageUtils';
import { MuseumPlan } from '@/shared/types';

type LocationFieldType = 'floor' | 'room';

export const findPlanIndexByTitle = (
  title: string,
  museumPlans: MuseumPlan[]
): number => {
  const floorIndex = parseInt(title, 10);
  if (!isNaN(floorIndex) && museumPlans[floorIndex]) {
    return floorIndex;
  }
  return museumPlans.findIndex((plan) => multilingualRecordHasValue(plan.title, title));
};

export const fillFloorFromPlan = (
  planIndex: number,
  museumPlans: MuseumPlan[],
  initialLanguages: ContentLanguageCode[]
): Record<ContentLanguageCode, string> => {
  const planTitle = museumPlans[planIndex].title;
  const result: Record<ContentLanguageCode, string> = {} as Record<ContentLanguageCode, string>;
  initialLanguages.forEach((lang) => {
    result[lang] = getMultilingualValueForLang(planTitle, lang) || `Étage ${planIndex + 1}`;
  });
  return result;
};

export const fillRoomFromPlans = (
  roomTitle: string,
  museumPlans: MuseumPlan[],
  initialLanguages: ContentLanguageCode[],
  floorHint?: Record<string, string>
): Record<ContentLanguageCode, string> | null => {
  let areasList: MuseumPlan['areas'][] = [];

  if (floorHint) {
    const floorReference = getMultilingualValue(floorHint);
    if (floorReference) {
      const planIndex = findPlanIndexByTitle(floorReference, museumPlans);
      if (planIndex !== -1 && museumPlans[planIndex].areas) {
        areasList = [museumPlans[planIndex].areas];
      }
    }
  }

  if (areasList.length === 0) {
    areasList = museumPlans.filter((plan) => plan.areas?.length).map((plan) => plan.areas);
  }

  for (const areas of areasList) {
    const areaIndex = areas.findIndex((area) => multilingualRecordHasValue(area.title, roomTitle));
    if (areaIndex !== -1) {
      const areaTitle = areas[areaIndex].title;
      const result: Record<ContentLanguageCode, string> = {} as Record<ContentLanguageCode, string>;
      initialLanguages.forEach((lang) => {
        result[lang] = getMultilingualValueForLang(areaTitle, lang) || `Salle ${areaIndex + 1}`;
      });
      return result;
    }
  }
  return null;
};

export const convertLocationToMultilingual = (
  value: string | Record<string, string> | undefined,
  initialLanguages: ContentLanguageCode[],
  fieldType: LocationFieldType,
  museumPlans?: MuseumPlan[],
  floorHint?: Record<string, string>
): Record<ContentLanguageCode, string> => {
  const result: Record<ContentLanguageCode, string> = {} as Record<ContentLanguageCode, string>;

  if (!value) return result;

  const fillMissingFromPlans = (referenceValue: string) => {
    if (!museumPlans?.length) return;

    if (fieldType === 'floor') {
      const planIndex = findPlanIndexByTitle(referenceValue, museumPlans);
      if (planIndex !== -1) {
        Object.assign(result, fillFloorFromPlan(planIndex, museumPlans, initialLanguages));
      }
      return;
    }

    const roomResult = fillRoomFromPlans(referenceValue, museumPlans, initialLanguages, floorHint);
    if (roomResult) {
      Object.assign(result, roomResult);
    }
  };

  const fillMissingWithReference = (referenceValue: string) => {
    initialLanguages.forEach((lang) => {
      if (!result[lang]) {
        result[lang] = referenceValue;
      }
    });
  };

  if (typeof value === 'object' && !Array.isArray(value)) {
    initialLanguages.forEach((lang) => {
      if (value[lang]) {
        result[lang] = value[lang];
      }
    });

    const missingLangs = initialLanguages.filter((lang) => !result[lang]);
    if (missingLangs.length > 0) {
      const referenceValue = getMultilingualValue(value);
      if (referenceValue) {
        fillMissingFromPlans(referenceValue);
        fillMissingWithReference(referenceValue);
      }
    }
    return result;
  }

  const stringValue = (value as unknown as string) || '';

  if (museumPlans?.length) {
    fillMissingFromPlans(stringValue);
    if (Object.keys(result).length > 0) {
      return result;
    }
  }

  initialLanguages.forEach((lang) => {
    result[lang] = stringValue;
  });
  return result;
};

export const getAvailableRoomsForFloor = (
  museumPlans: MuseumPlan[] | undefined,
  floor: Record<string, string>
) => {
  if (!museumPlans) return [];
  const floorReference = getMultilingualValue(floor, DEFAULT_LANGUAGE);
  if (!floorReference) return [];
  const planIndex = findPlanIndexByTitle(floorReference, museumPlans);
  if (planIndex === -1) return [];
  return museumPlans[planIndex]?.areas || [];
};
